import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from './clients/ApiClients';
import { setToken, setRefreshToken } from './utils/TokenUtils';
import { WORKOS_STATE_KEY } from './utils/workosConfig';
import { apiErrorStatus } from './utils/apiErrors';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import { SignInShell } from './shell/SignInShell';

/**
 * Whether a saved return path belongs to the internal portal, which
 * front-porch's router does not serve. Only a same-origin path counts —
 * `//host/internal` is protocol-relative and would leave the site.
 */
export const isInternalPortalPath = (pathname: unknown): boolean =>
  typeof pathname === 'string' &&
  !pathname.startsWith('//') &&
  (pathname === '/internal' || pathname.startsWith('/internal/'));

/**
 * Where AuthKit returns the browser after a sign-in.
 *
 * The page's only job is to hand the authorization code to our backend
 * and put the tokens it returns where the rest of the app looks for them.
 * It deliberately does **not** exchange the code itself: that exchange
 * carries our WorkOS secret key, so it can only happen server-side.
 *
 * The tokens saved here are ours, not WorkOS's. The backend discards
 * WorkOS's session during the exchange and mints its own pair from the
 * `users` row, so everything downstream — refresh, revocation, the
 * `token_version` check on every request — behaves exactly as it does
 * after a password sign-in.
 */
const WorkosCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string>('');

  // React 18 mounts effects twice in development. The authorization code
  // is single-use, so a second exchange always fails — and would replace
  // a good result with an error. One attempt per page, enforced here.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    const code = searchParams.get('code');
    const returnedState = searchParams.get('state');
    const expectedState = sessionStorage.getItem(WORKOS_STATE_KEY);
    sessionStorage.removeItem(WORKOS_STATE_KEY);

    // AuthKit reports its own failures this way — a cancelled sign-in, a
    // rejected consent. Nothing to exchange, and not an error worth
    // alarming anyone about.
    const authkitError = searchParams.get('error');
    if (authkitError) {
      navigate('/login', { replace: true });
      return;
    }

    if (!code) {
      setError('Sign-in did not complete. Please try again.');
      return;
    }

    // The check that makes the round trip meaningful. A code that arrives
    // without the `state` we issued is one we did not ask for, which is
    // what login CSRF looks like: an attacker feeding a victim a code
    // from the attacker's own AuthKit session so the victim lands in the
    // attacker's account.
    if (!returnedState || returnedState !== expectedState) {
      setError('Sign-in could not be verified. Please try again.');
      return;
    }

    const exchange = async () => {
      try {
        const response = await authService.workosLogin({
          workosLoginRequest: { code },
        });

        const data = response.data as {
          access_token?: string;
          refresh_token?: string;
        };

        if (!data.access_token) {
          throw new Error('Access token not found in response');
        }

        // Session-scoped rather than remembered. The "remember me"
        // checkbox lives on the login form and this flow left that page
        // behind; defaulting to the narrower choice is the right way to
        // be wrong.
        if (data.refresh_token) {
          setRefreshToken(data.refresh_token, false);
        }
        setToken(data.access_token, false);

        const savedLocation = sessionStorage.getItem('returnTo');
        if (savedLocation) {
          const { pathname, search, hash } = JSON.parse(savedLocation);
          sessionStorage.removeItem('returnTo');
          const target = `${pathname ?? ''}${search ?? ''}${hash ?? ''}`;
          if (isInternalPortalPath(pathname)) {
            // The internal portal is another pod on this host (the
            // ingress routes /internal/ to it), so the router cannot
            // reach it: a full page load does. Same-origin paths only.
            window.location.replace(target);
          } else {
            navigate(target, { replace: true });
          }
        } else {
          navigate('/home', { replace: true });
        }
      } catch (err) {
        // A 401 here is most often an identity WorkOS is happy with that
        // has no account on this side — we never create one. Say that
        // plainly instead of "invalid credentials", which sends people
        // to reset a password that was never the problem.
        console.error('WorkOS login failed:', err);
        if (apiErrorStatus(err) === 401) {
          setError(
            'That account is not set up for this portal. Please contact your administrator.'
          );
        } else {
          setError('Unable to sign in right now. Please try again.');
        }
      }
    };

    exchange();
  }, [navigate, searchParams]);

  // Inside the same shell as `LoginPage`, so the hop from one to the
  // other changes only the panel's content.
  return (
    <SignInShell>
      <div className="mx-auto flex max-w-sm flex-col items-center px-4 py-24 text-center">
        {error ? (
          <>
            <ErrorAlert className="w-full text-left">{error}</ErrorAlert>
            <button
              type="button"
              onClick={() => navigate('/login', { replace: true })}
              className="mt-5 inline-flex items-center rounded-md border border-line px-2.5 py-1 text-[12px] font-medium text-fg-secondary hover:border-line-strong hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              Back to sign in
            </button>
          </>
        ) : (
          <p className="text-[13px]/5 text-fg-muted">Signing you in…</p>
        )}
      </div>
    </SignInShell>
  );
};

export default WorkosCallbackPage;
